import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { api, ApiError } from '@/lib/api-client'
import { tokenStore } from '@/lib/tokens'
import type { AuthResponse, SessionUser } from '@/types/api'

export const ME_QUERY_KEY = ['auth', 'me'] as const

interface RegisterInput {
  email: string
  password: string
  firstName: string
  lastName: string
  // Self-registration only ever creates a learner — instructor accounts are
  // admin-invite-only (see accept-invite-page.tsx).
  role?: 'learner'
}

interface AcceptInviteInput {
  token: string
  firstName: string
  lastName: string
  password: string
}

interface AuthContextValue {
  user: SessionUser | null
  status: 'loading' | 'authenticated' | 'unauthenticated'
  login: (email: string, password: string) => Promise<SessionUser>
  register: (input: RegisterInput) => Promise<void>
  acceptInvite: (input: AcceptInviteInput) => Promise<void>
  verifyEmail: (code: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function hasStoredSession() {
  return Boolean(tokenStore.getAccessToken() || tokenStore.getRefreshToken())
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [hasToken, setHasToken] = useState(hasStoredSession)

  useEffect(
    () => tokenStore.subscribe(() => setHasToken(hasStoredSession())),
    [],
  )

  const meQuery = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: () => api.get<SessionUser>('/auth/me'),
    enabled: hasToken,
    retry: false,
    staleTime: Infinity,
  })

  useEffect(() => {
    if (hasToken && meQuery.isError && meQuery.error instanceof ApiError) {
      tokenStore.clear()
    }
  }, [hasToken, meQuery.isError, meQuery.error])

  const applyAuthResponse = useCallback(
    (response: AuthResponse) => {
      const sessionUser: SessionUser = {
        sub: response.user.id,
        email: response.user.email,
        role: response.user.role,
        isVerified: response.user.isVerified,
      }
      tokenStore.set(response)
      queryClient.setQueryData<SessionUser>(ME_QUERY_KEY, sessionUser)
      return sessionUser
    },
    [queryClient],
  )

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await api.post<AuthResponse>(
        '/auth/login',
        { email, password },
        { skipAuth: true },
      )
      return applyAuthResponse(response)
    },
    [applyAuthResponse],
  )

  const register = useCallback(
    async (input: RegisterInput) => {
      const response = await api.post<AuthResponse>('/auth/register', input, {
        skipAuth: true,
      })
      applyAuthResponse(response)
    },
    [applyAuthResponse],
  )

  const acceptInvite = useCallback(
    async (input: AcceptInviteInput) => {
      const response = await api.post<AuthResponse>(
        '/auth/accept-invite',
        input,
        { skipAuth: true },
      )
      applyAuthResponse(response)
    },
    [applyAuthResponse],
  )

  const verifyEmail = useCallback(
    async (code: string) => {
      const response = await api.post<AuthResponse>(
        '/auth/verify-email',
        { token: code },
        { skipAuth: true },
      )
      applyAuthResponse(response)
    },
    [applyAuthResponse],
  )

  const logout = useCallback(async () => {
    const refreshToken = tokenStore.getRefreshToken()
    tokenStore.clear()
    queryClient.setQueryData(ME_QUERY_KEY, null)
    queryClient.clear()
    if (refreshToken) {
      await api
        .post('/auth/logout', { refreshToken }, { skipAuth: true })
        .catch(() => undefined)
    }
  }, [queryClient])

  const status: AuthContextValue['status'] = !hasToken
    ? 'unauthenticated'
    : meQuery.isPending
      ? 'loading'
      : meQuery.data
        ? 'authenticated'
        : 'unauthenticated'

  return (
    <AuthContext.Provider
      value={{
        user: meQuery.data ?? null,
        status,
        login,
        register,
        acceptInvite,
        verifyEmail,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
